import React, { useState, useEffect, useMemo, useContext, useRef } from "react";
import moment from "moment";
import AllDrawServices from "../../services/AllDrawServices";
import "../../styles/AllDraw.css";
import Loader from "../../components/Loader";
import useActiveGames from "../../hooks/useActiveGames";
import NoData from "../../components/NoData";
import GameSelect from "../../components/GameSelect";
import { FiArrowLeft } from "react-icons/fi";
import { useHistory } from "react-router-dom";
import { MdPreview } from "react-icons/md";
import { AuthContext } from "../../context/AuthContext";
import {
  getStoredAllDrawDate,
  setStoredAllDrawDate,
  getAllDrawCache,
  setAllDrawCache,
} from "../../utils/utils";
import {
  getYantraImageUrl,
  getYantraName,
  useOutletTemplateImages,
} from "../../utils/yantraImageTemplate";
import JackpotImage from "../../assets/101728963.jfif";
import JackpotImage2 from "../../assets/829075774018b36ebb587.gif";

// Up to this many games fit as a row of tabs; past it a picker takes over.
const MAX_GAME_TABS = 3;

const TIME_DISPLAY_FORMAT = "hh:mm A";

const getTodayDateString = () => moment.utc().format("YYYY-MM-DD");

const formatJackpotBadgeLabel = (winnerType, winnerX) => {
  const wt = String(winnerType || "").toUpperCase();
  if (wt === "JACKPOT") return "Jackpot";
  if (wt === "JACKPOTX") {
    const raw = String(winnerX || "").trim();
    if (!raw) return "Jackpot";
    if (/[xX]$/.test(raw)) return `Jackpot (${raw})`;
    return `Jackpot (${raw}x)`;
  }
  return null;
};

const AllDraw = () => {
  const history = useHistory();
  const api = new AllDrawServices();
  const scrollContainerRef = useRef(null);
  const pendingScrollRestore = useRef(null);

  const { user } = useContext(AuthContext);
  // Tabs come from the running games. The choice is remembered, and if the
  // game last looked at has since been stopped the hook moves to one that
  // is still running rather than leaving a dead tab selected.
  const {
    games: activeGames,
    loading: gamesLoading,
    activeCode: gameType,
    setActiveCode: setGameType,
  } = useActiveGames();
  const [selectedDate, setSelectedDate] = useState(getStoredAllDrawDate);
  const [draws, setDraws] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Outlet users only: enable templated yantra images on this page too.
  // The hook fetches at most once per mount (per user_id/role) and updates a
  // shared module-level override consumed by getYantraImageUrl below.
  // eslint-disable-next-line no-unused-vars
  const templateImageVersion = useOutletTemplateImages(user);

  const maxDate = useMemo(() => getTodayDateString(), []);

  const fetchDraws = async () => {
    try {
      setLoading(true);
      setError(null);
      // The game list arrives a moment after mount; asking for game "" just
      // comes back as a validation error.
      if (!gameType) return;
      const response = await api.getDrawsByDate(selectedDate, gameType);
      if (response?.isSuccess && Array.isArray(response?.data)) {
        setDraws(response.data);
      } else {
        setDraws([]);
      }
    } catch (error) {
      setError(error?.response?.data?.message || "Failed to load draws");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setStoredAllDrawDate(selectedDate);
  }, [selectedDate]);

  useEffect(() => {
    const cache = getAllDrawCache();
    const cacheMatches =
      cache &&
      cache.date === selectedDate &&
      cache.gameType === gameType &&
      Array.isArray(cache.draws) &&
      cache.draws.length > 0;

    if (cacheMatches) {
      setDraws(cache.draws);
      setLoading(false);
      setError(null);
      if (Number.isFinite(cache.scrollTop) && cache.scrollTop > 0) {
        pendingScrollRestore.current = cache.scrollTop;
      }
      return;
    }
    fetchDraws();
  }, [selectedDate, gameType]);

  useEffect(() => {
    if (
      !loading &&
      draws.length > 0 &&
      pendingScrollRestore.current != null &&
      scrollContainerRef.current
    ) {
      const top = pendingScrollRestore.current;
      pendingScrollRestore.current = null;
      scrollContainerRef.current.scrollTop = top;
    }
  }, [loading, draws.length]);

  const handleDateChange = (e) => {
    const v = e.target.value;
    if (!v) return;
    if (v > maxDate) return;
    setSelectedDate(v);
  };

  const handleBack = () => {
    history.go(-1);
  };

  return (
    <div className="all-draw-page">
      <header className="all-draw-header">
        <div className="all-draw-filters">
          <h5 className="all-draw-title">
            <button
              type="button"
              className="all-draw-back-button"
              onClick={handleBack}
              aria-label="Go back"
            >
              <FiArrowLeft size={20} />
            </button>
            All Draw
          </h5>
          <div className="all-draw-filter-row">
            <div className="all-draw-tabs">
              {gamesLoading ? (
                <span className="all-draw-tab-empty">Loading games...</span>
              ) : activeGames.length === 0 ? (
                <span className="all-draw-tab-empty">No games running</span>
              ) : activeGames.length > MAX_GAME_TABS ? (
                <GameSelect
                  games={activeGames}
                  value={gameType}
                  onChange={setGameType}
                />
              ) : (
                activeGames.map((g) => (
                  <button
                    key={g.id}
                    type="button"
                    className={`all-draw-tab ${
                      gameType === g.game_code ? "active" : ""
                    }`}
                    onClick={() => setGameType(g.game_code)}
                    title={`${g.total_ghar} ghar`}
                  >
                    {g.game_name}
                  </button>
                ))
              )}
            </div>
            <div className="all-draw-date-wrap">
              <label htmlFor="all-draw-date">Date</label>
              <input
                id="all-draw-date"
                type="date"
                value={selectedDate}
                max={maxDate}
                onChange={handleDateChange}
                aria-label="Select date (today or past)"
              />
            </div>
          </div>
        </div>
      </header>

      {loading && <Loader message="Loading draws..." />}
      {!loading && error && <div className="all-draw-error">{error}</div>}
      {!loading && !error && draws.length === 0 && (
        <NoData
          message="No draws for this date and game type."
          subText="Please try again later"
        />
      )}
      {!loading && !error && draws.length > 0 && (
        <div className="all-draw-scroll-container" ref={scrollContainerRef}>
          <div className="all-draw-list">
            {draws.flatMap((item) => {
              const endTime = item?.end_time
                ? moment.utc(item.end_time).local()
                : null;
              const dateLabel = endTime ? endTime.format("DD-MM-YYYY") : "";
              const timeLabel = endTime
                ? endTime.format(TIME_DISPLAY_FORMAT)
                : "";
              const winnerType = String(item?.winner_type || "").toUpperCase();
              const isJackpot = winnerType === "JACKPOT" ? true : false;
              const jackpotBadgeLabel =
                winnerType === "JACKPOT"
                  ? "Multi Winner"
                  : winnerType === "JACKPOTX"
                    ? formatJackpotBadgeLabel(winnerType, item?.winner_x)
                    : null;
              const winners =
                Array.isArray(item?.winner_yantras) &&
                item.winner_yantras.length > 0
                  ? item.winner_yantras
                  : [
                      {
                        yantra_name: item?.yantra_name,
                        yantra_img: item?.yantra_img,
                        yantra_id: item?.winner_yantra,
                      },
                    ];
              const participated = item.participated === true;
              const profitLoss =
                participated && Number.isFinite(item.profit_loss)
                  ? item.profit_loss
                  : null;

              if (isJackpot) {
                return [
                  <div key={`${item.session_id}-jackpot`} className="neon-card">
                    <div className="neon-content">
                      <div className="outlet-live-session-winners-scroll">
                        {winners.map((w, wIdx) => {
                          const imgSrc = getYantraImageUrl(
                            w?.yantra_img,
                            w?.yantra_id,
                          );
                          const wName = getYantraName(w?.yantra_name, w?.yantra_id) || "Yantra";
                          return (
                            <div
                              key={`${item.session_id}-${w?.yantra_id ?? wIdx}`}
                              className="outlet-live-session-winner-item"
                            >
                              <div className="outlet-live-session-winner-item-img">
                                <img
                                  src={imgSrc}
                                  alt={wName}
                                />
                              </div>
                              <div className="outlet-live-session-winner-item-name">
                                {wName}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <hr className="outlet-history-divider" />
                      <div>
                        <div className="multi-winner-bar">
                          <span>MULTI WINNER</span>
                        </div>
                        <div className="outlet-multi-winner-date">
                          <span>
                            {dateLabel} {timeLabel}
                          </span>
                        </div>
                      </div>
                      {item.session_no != null && (
                        <div className="all-draw-card-session">
                          <span className="all-draw-card-session-number">
                            SN#{item.session_no}{" "}
                            {(user?.role_id === 1 || (user?.role_id === 2 || user?.role_id === 5)) && (
                              <button
                                type="button"
                                className="all-draw-card-session-number-btn"
                                title="View winning detail"
                                onClick={() => {
                                  try {
                                    window.sessionStorage.setItem(
                                      "swd_session_id",
                                      String(item.session_id),
                                    );
                                    setStoredAllDrawDate(selectedDate);
                                    const scrollEl = document.querySelector(
                                      ".all-draw-scroll-container",
                                    );
                                    const scrollTop = scrollEl
                                      ? scrollEl.scrollTop
                                      : 0;
                                    setAllDrawCache(
                                      selectedDate,
                                      gameType,
                                      draws,
                                      scrollTop,
                                    );
                                  } catch (_) {}
                                  history.push("/session-winning-detail");
                                }}
                              >
                                <MdPreview size={18} />
                              </button>
                            )}
                          </span>
                        </div>
                      )}
                      {(user?.role_id === 1 || (user?.role_id === 2 || user?.role_id === 5)) &&
                        item.total_participants != null &&
                        item.total_participants > 0 && (
                          <div className="all-draw-card-profit profit">
                            Total Participants: {item.total_participants}
                          </div>
                        )}
                      {participated && item.scan_pending && (
                        <div className="all-draw-card-profit scan-pending">
                          Scan Pending
                        </div>
                      )}
                      {participated &&
                        !item.scan_pending &&
                        profitLoss !== null && (
                          <div
                            className={`all-draw-card-profit ${profitLoss >= 0 ? "profit" : "loss"}`}
                          >
                            {profitLoss >= 0 ? "+" : ""}₹
                            {Number(profitLoss).toFixed(2)}
                          </div>
                        )}
                    </div>
                  </div>,
                ];
              }

              return winners.map((w, wIdx) => {
                const nameLabel =
                  getYantraName(w?.yantra_name, w?.yantra_id) ||
                  (w?.yantra_id ? `Yantra ${w.yantra_id}` : "Yantra");
                const imgSrc = getYantraImageUrl(w?.yantra_img, w?.yantra_id);
                // The key belongs on the fragment - it is what the map returns.
                const winnerKey = `${item.session_id}-${w?.yantra_id ?? wIdx}`;
                return winnerType === "JACKPOTX" ? (
                  <React.Fragment key={winnerKey}>
                    <div>
                      <div className="outlet-live-jackpot-screen">
                        <div className="outlet-live-jackpot-card">
                          <div
                            className="outlet-live-jackpot-content"
                            style={{
                              "--outlet-jackpot2-bg": `url(${JackpotImage2})`,
                            }}
                          >
                            <div className="outlet-history-top">
                              <div className="outlet-live-jackpot-yantra">
                                <img
                                  loading="lazy"
                                  src={imgSrc}
                                  alt={nameLabel}
                                />
                              </div>
                              <div
                                className={`outlet-history-badge jackpotx`}
                                title={`jackpotx`}
                              >
                                <div className="outlet-history-badge-img-wrap">
                                  <img
                                    src={JackpotImage}
                                    alt=""
                                    className="outlet-history-badge-brand"
                                    decoding="async"
                                  />
                                </div>
                                <div className="outlet-history-badge-copy">
                                  <span className="outlet-history-badge-sub">
                                    {jackpotBadgeLabel}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="outlet-live-jackpot-footer">
                              <div className="outlet-live-jackpot-footer-yantra">
                                {nameLabel}
                              </div>
                              <div className="outlet-live-jackpot-footer-time">
                                {dateLabel} {timeLabel}
                              </div>
                              {item.session_no != null && (
                                <div className="all-draw-card-session">
                                  <span className="all-draw-card-session-number">
                                    SN#{item.session_no}{" "}
                                    {(user?.role_id === 1 ||
                                      (user?.role_id === 2 || user?.role_id === 5)) && (
                                      <button
                                        type="button"
                                        className="all-draw-card-session-number-btn"
                                        title="View winning detail"
                                        onClick={() => {
                                          try {
                                            window.sessionStorage.setItem(
                                              "swd_session_id",
                                              String(item.session_id),
                                            );
                                            setStoredAllDrawDate(selectedDate);
                                            const scrollEl =
                                              document.querySelector(
                                                ".all-draw-scroll-container",
                                              );
                                            const scrollTop = scrollEl
                                              ? scrollEl.scrollTop
                                              : 0;
                                            setAllDrawCache(
                                              selectedDate,
                                              gameType,
                                              draws,
                                              scrollTop,
                                            );
                                          } catch (_) {}
                                          history.push(
                                            "/session-winning-detail",
                                          );
                                        }}
                                      >
                                        <MdPreview size={18} />
                                      </button>
                                    )}
                                  </span>
                                </div>
                              )}
                              {(user?.role_id === 1 || (user?.role_id === 2 || user?.role_id === 5)) &&
                                item.total_participants != null &&
                                item.total_participants > 0 && (
                                  <div className="all-draw-card-profit profit">
                                    Total Participants:{" "}
                                    {item.total_participants}
                                  </div>
                                )}
                              {participated && item.scan_pending && (
                                <div className="all-draw-card-profit scan-pending">
                                  Scan Pending
                                </div>
                              )}
                              {participated &&
                                !item.scan_pending &&
                                profitLoss !== null && (
                                  <div
                                    className={`all-draw-card-profit ${profitLoss >= 0 ? "profit" : "loss"}`}
                                  >
                                    {profitLoss >= 0 ? "+" : ""}₹
                                    {Number(profitLoss).toFixed(2)}
                                  </div>
                                )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                ) : (
                  <React.Fragment key={winnerKey}>
                    <div className={`all-draw-card`}>
                      <div className="all-draw-card-top">
                        <div className="all-draw-card-dot">
                          {imgSrc ? <img src={imgSrc} alt={nameLabel} /> : null}
                        </div>
                        <div className="all-draw-card-datetime">
                          <span className="all-draw-card-date">
                            {dateLabel}
                          </span>
                          <span className="all-draw-card-time">
                            {timeLabel}
                          </span>
                        </div>
                        {/* {jackpotBadgeLabel && (
                          <div
                            className={`all-draw-winner-badge ${
                              winnerType === "JACKPOTX" ? "jackpotx" : "jackpot"
                            }`}
                            title={jackpotBadgeLabel}
                          >
                            <div className="all-draw-winner-badge-img-wrap">
                              <img
                                src={JackpotImage}
                                alt=""
                                className="all-draw-winner-badge-brand"
                                decoding="async"
                              />
                            </div>
                            <div className="all-draw-winner-badge-copy">
                              <span className="all-draw-winner-badge-sub">
                                {jackpotBadgeLabel}
                              </span>
                            </div>
                          </div>
                        )} */}
                      </div>
                      <div className="all-draw-card-divider" />
                      <div className="all-draw-card-name">{nameLabel}</div>
                      {item.session_no != null && (
                        <div className="all-draw-card-session">
                          <span className="all-draw-card-session-number">
                            SN#{item.session_no}{" "}
                            {(user?.role_id === 1 || (user?.role_id === 2 || user?.role_id === 5)) && (
                              <button
                                type="button"
                                className="all-draw-card-session-number-btn"
                                title="View winning detail"
                                onClick={() => {
                                  try {
                                    window.sessionStorage.setItem(
                                      "swd_session_id",
                                      String(item.session_id),
                                    );
                                    setStoredAllDrawDate(selectedDate);
                                    const scrollEl = document.querySelector(
                                      ".all-draw-scroll-container",
                                    );
                                    const scrollTop = scrollEl
                                      ? scrollEl.scrollTop
                                      : 0;
                                    setAllDrawCache(
                                      selectedDate,
                                      gameType,
                                      draws,
                                      scrollTop,
                                    );
                                  } catch (_) {}
                                  history.push("/session-winning-detail");
                                }}
                              >
                                <MdPreview size={18} />
                              </button>
                            )}
                          </span>
                        </div>
                      )}
                      {(user?.role_id === 1 || (user?.role_id === 2 || user?.role_id === 5)) &&
                        item.total_participants != null &&
                        item.total_participants > 0 && (
                          <div className="all-draw-card-profit profit">
                            Total Participants: {item.total_participants}
                          </div>
                        )}
                      {participated && item.scan_pending && (
                        <div className="all-draw-card-profit scan-pending">
                          Scan Pending
                        </div>
                      )}
                      {participated &&
                        !item.scan_pending &&
                        profitLoss !== null && (
                          <div
                            className={`all-draw-card-profit ${profitLoss >= 0 ? "profit" : "loss"}`}
                          >
                            {profitLoss >= 0 ? "+" : ""}₹
                            {Number(profitLoss).toFixed(2)}
                          </div>
                        )}
                    </div>
                  </React.Fragment>
                );
              });
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AllDraw;
